import React, { createContext, useContext, useState, useEffect, useCallback } from "react";

export const CartContext = createContext();

export function CartProvider({ children, user }) {
  const [cartItems, setCartItems] = useState([]);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const loadServerCart = useCallback(async () => {
    if (!user) {
      setCartItems([]);
      return;
    }

    try {
      const res = await fetch("https://ecomapi.asherjewels.com.ph/apiecom/get_cart.php", {
        credentials: "include",
      });
      const json = await res.json();
      if (json.status === "success" && json.data) {
        setCartItems(
          json.data.map((item) => ({
            id: item.product_id,
            name: item.name,
            pricePHP: item.price,
            qty: item.quantity,
            imageUrl: item.image_url || "",
            product_code: item.product_code || "",
          }))
        );
      } else {
        setCartItems([]);
      }
    } catch (err) {
      console.error("Failed to load server cart:", err);
    }
  }, [user]);

  useEffect(() => {
    loadServerCart();
  }, [loadServerCart]);

  const addToCart = async (product) => {
    if (!user) {
      return { requiresAuth: true };
    }

    const alreadyExists = cartItems.some((item) => String(item.id) === String(product.id));
    if (alreadyExists) {
      return { ok: true, skipped: true };
    }

    try {
      await fetch("https://ecomapi.asherjewels.com.ph/apiecom/add_to_cart.php", {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: new URLSearchParams({
          product_id: product.id,
          product_code: product.product_code,
          quantity: 1,
          name: product.name,
          image_url: product.imageUrl,
        }),
      });

      await loadServerCart();
      return { ok: true };
    } catch (err) {
      console.error("Error adding item to server cart:", err);
      return { ok: false };
    }
  };

  const removeFromCart = async (id) => {
    if (!user) {
      setCartItems((prevItems) => prevItems.filter((item) => item.id !== id));
      return;
    }

    try {
      const response = await fetch("https://ecomapi.asherjewels.com.ph/apiecom/remove_from_cart.php", {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          Accept: "application/json",
        },
        body: new URLSearchParams({ product_id: id }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Server responded with status ${response.status}: ${errorText}`);
      }

      setCartItems((prevItems) => prevItems.filter((item) => item.id !== id));
      await loadServerCart();
    } catch (err) {
      console.error("removeFromCart error:", err);
      setCartItems((prevItems) => prevItems.filter((item) => item.id !== id));
    }
  };

  const clearCart = () => {
    setCartItems([]);
  };

  const toggleSidebar = () => setSidebarOpen((open) => !open);
  const openSidebar = () => setSidebarOpen(true);
  const closeSidebar = () => setSidebarOpen(false);

  useEffect(() => {
    window.loadServerCart = loadServerCart;
  }, [loadServerCart]);

  return (
    <CartContext.Provider
      value={{
        cartItems,
        addToCart,
        removeFromCart,
        loadServerCart,
        clearCart,
        sidebarOpen,
        toggleSidebar,
        openSidebar,
        closeSidebar,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const context = useContext(CartContext);
  if (!context) throw new Error("useCart must be used within CartProvider");
  return context;
}
